/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Appointment reschedule time calculation and API update for ViewAppointments.
 * @module logic/appointments/appointmentReschedule
 */

import { updateAppointment } from "../appointment/index.js";

/** @param {Object} eventData @returns {number} Duration in minutes (defaults to 60). */
export const getAppointmentDurationMinutes = (eventData) => {
  if (!eventData?.duracionCitas) {
    return 60;
  }

  const [hours, minutes] = eventData.duracionCitas.split(":").map(Number);
  return hours * 60 + minutes;
};

export const buildRescheduleTimes = ({ newDate, newTime, eventData }) => {
  const durationMinutes = getAppointmentDurationMinutes(eventData);
  const newStartTime = new Date(newDate);
  const [newHour, newMinute] = newTime.split(":").map(Number);
  newStartTime.setHours(newHour, newMinute, 0, 0);

  const newEndTime = new Date(newStartTime.getTime() + durationMinutes * 60 * 1000);
  const formattedNewDate = newDate.toISOString().split("T")[0];
  const formattedNewStartTime = `${String(newHour).padStart(2, "0")}:${String(newMinute).padStart(2, "0")}`;
  const formattedNewEndTime = `${String(newEndTime.getHours()).padStart(2, "0")}:${String(newEndTime.getMinutes()).padStart(2, "0")}`;

  return {
    formattedNewDate,
    formattedNewStartTime,
    formattedNewEndTime,
    newStartTime,
  };
};

/**
 * Reschedules an appointment and returns the updated schedule fields.
 */
export const rescheduleAppointment = async ({
  navigate,
  appointment,
  eventData,
  newDate,
  newTime,
}) => {
  if (!appointment.horaInicio || !appointment.horaFin) {
    return { skipped: true, reason: "missing_times" };
  }

  const schedule = buildRescheduleTimes({ newDate, newTime, eventData });
  const updatedAppointment = await updateAppointment(
    navigate,
    appointment._id,
    eventData._id,
    {
      appointmentInformation: {
        fecha: schedule.formattedNewDate,
        horaInicio: schedule.formattedNewStartTime,
        horaFin: schedule.formattedNewEndTime,
        fechaHoraInicioPlanificada: schedule.newStartTime.toISOString(),
      },
    },
  );

  if (!updatedAppointment) {
    return { skipped: true, reason: "update_failed" };
  }

  return {
    skipped: false,
    schedule: {
      fecha: schedule.formattedNewDate,
      horaInicio: schedule.formattedNewStartTime,
      horaFin: schedule.formattedNewEndTime,
    },
  };
};
